import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useDealerContext } from "@/lib/useDealerContext";
import { variantMatches, variantIdentity } from "@/lib/searchNormalize";
import { productTitle } from "@/lib/productNaming";

// V4.7 SAMPLE-158: business units — loose tile (4×4 / 2×6) and sheet (2×2).
// The 2×2 ships on a sheet but is never described as a mosaic.
const UNIT_OPTIONS = ["loose tile", "sheet"];
// ID-063: mesh-mounted mosaic collection variations also ship as sheets
const defaultUnit = (v) => (v?.format_in === "2x2" || v?.design_id || v?.category === "mosaic" ? "sheet" : "loose tile");

const NEW_PROJECT = "__new__";
const PROJECT_TYPES = ["Residential", "Commercial", "Hospitality", "Other"];
const TIMEFRAMES = ["ASAP / less than 30 days", "1-3 months", "3-6 months", "6-12 months", "12+ months", "Not sure yet"];

const EMPTY_NP = { name: "", client_name: "", line1: "", line2: "", city: "", state: "", zip: "", sqft: "", project_type: "", expected_timeframe: "", notes: "" };
const EMPTY_RC = { name: "", line1: "", line2: "", city: "", state: "", zip: "", phone: "" };

const labelCls = "text-xs font-semibold uppercase tracking-wider text-warmgrey";
const inputCls = "h-11 bg-white focus-visible:ring-terracotta";
const pid_project_selected = (pid) => pid && pid !== NEW_PROJECT;

function VariantSearch({ catalog, onPick, testid }) {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  // §29.3: deterministic normalized search-as-you-type — never a raw 99-variant scroll
  const matches = (q.trim() ? catalog.filter((v) => variantMatches(v, q)) : catalog).slice(0, 12);
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder='Search — MZ001, 001, HRECH, 4x4, "two by six"…'
        data-testid={testid} className={inputCls} />
      {focused && (
        <div className="absolute z-20 mt-1 max-h-56 w-full min-w-[16rem] overflow-y-auto rounded-md border border-border bg-white shadow-lg" data-testid={`${testid}-results`}>
          {matches.length === 0 && <p className="p-3 text-xs text-muted-foreground">No matching variants</p>}
          {matches.map((v) => (
            <button key={v.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(v.id); setQ(""); setFocused(false); }} data-testid={`${testid}-option-${v.id}`}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-sand">
              {v.image_urls?.[0] ? (
                <img src={v.image_urls[0]} alt="" className="h-9 w-9 shrink-0 rounded border border-border object-cover" />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-border bg-sand font-mono text-[9px] text-warmgrey">{(v.color_code || "").replace("MZ", "")}</span>
              )}
              <span className="min-w-0 flex-1 truncate text-charcoal" data-testid={`${testid}-identity-${v.id}`}>
                <span className="font-mono text-xs text-terracotta">{v.sku}</span> · {variantIdentity(v)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SampleRequestDialog({ open, onOpenChange, catalog, projects, preselectedVariantId = "", preselectedProjectId = "", context = "", onSubmitted, onProjectCreated, editing = null }) {
  const ctxVariant = preselectedVariantId && !editing ? catalog.find((v) => v.id === preselectedVariantId) : null;
  const [mode, setMode] = useState("showroom");
  const [lines, setLines] = useState([{ variant_id: "", quantity: 1, quantity_unit: "loose tile" }]);
  const [projectId, setProjectId] = useState("");
  const [localProjects, setLocalProjects] = useState([]);
  const [np, setNp] = useState(EMPTY_NP);
  const [shipTo, setShipTo] = useState("showroom");
  const [rc, setRc] = useState(EMPTY_RC);
  const [idemKey, setIdemKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [allowance, setAllowance] = useState(null);
  const { locations, isManager, myLocationId, defaultLocationId, showLocationPicker, locLabel } = useDealerContext();
  const [locationId, setLocationId] = useState("");

  useEffect(() => {
    if (open) setLocationId(isManager ? (myLocationId || "") : (defaultLocationId || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isManager, myLocationId, defaultLocationId]);

  const allProjects = [...localProjects, ...projects.filter((p) => !localProjects.some((lp) => lp.id === p.id))];
  // ID-008: started from an open project → that project is the context; no project selection / creation step
  const lockedProject = !editing && preselectedProjectId ? allProjects.find((p) => p.id === preselectedProjectId) : null;

  useEffect(() => {
    if (open && editing) {
      // ID-012: amend an existing request (24h window) — prefill from the saved record, same request ID
      setMode(editing.project_id ? "project" : "showroom");
      setLines(editing.lines.map((l) => ({ variant_id: l.variant_id, quantity: l.quantity, quantity_unit: l.quantity_unit || "loose tile" })));
      setProjectId(editing.project_id || "");
      setLocalProjects([]);
      setNp(EMPTY_NP);
      const r = editing.recipient || {};
      setShipTo(editing.is_direct_to_client ? "other" : "showroom");
      setRc(editing.is_direct_to_client ? { name: r.name || "", line1: r.line1 || "", line2: r.line2 || "", city: r.city || "", state: r.state || "", zip: r.postal_code || "", phone: r.phone || "" } : EMPTY_RC);
      if (editing.location_id) setLocationId(editing.location_id);
      api.get("/samples/allowance").then((res) => setAllowance(res.data)).catch(() => setAllowance(null));
      return;
    }
    if (open) {
      setMode(preselectedProjectId ? "project" : "showroom");
      setLines([{ variant_id: preselectedVariantId, quantity: 1, quantity_unit: defaultUnit(catalog.find((v) => v.id === preselectedVariantId)) }]);
      setProjectId(preselectedProjectId || "");
      setLocalProjects([]);
      setNp(EMPTY_NP);
      setShipTo("showroom");
      setRc(EMPTY_RC);
      setIdemKey(crypto.randomUUID());
      api.get("/samples/allowance").then((r) => setAllowance(r.data)).catch(() => setAllowance(null));
    }
  }, [open, preselectedVariantId, editing?.id]);

  // §29.7: "Use project address" — authoritative project address is offered, never retyped
  const selectedProject = allProjects.find((x) => x.id === projectId);
  const projectAddr = selectedProject?.shipping_address;
  useEffect(() => {
    if (mode === "project" && shipTo === "project" && projectAddr) {
      setRc({
        name: projectAddr.recipient_name || selectedProject.client_name || "",
        line1: projectAddr.line1 || "",
        line2: projectAddr.line2 || "",
        city: projectAddr.city || "",
        state: projectAddr.state || "",
        zip: projectAddr.postal_code || "",
        phone: projectAddr.phone || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, shipTo, mode]);

  // §29.7: ZIP assistance — suggests city/state, never overwrites explicit input
  const zipAssist = (zip, current, apply) => {
    if (!/^\d{5}$/.test(zip)) return;
    api.get(`/dealer/zip-lookup?zip=${zip}`)
      .then(({ data }) => {
        const patch = {};
        if (!current.city.trim() && data.city) patch.city = data.city;
        if (!current.state.trim() && data.state) patch.state = data.state;
        if (Object.keys(patch).length) apply(patch);
      })
      .catch(() => {});
  };

  const setNpField = (k) => (e) => setNp((s) => ({ ...s, [k]: e.target ? e.target.value : e }));
  const setRcField = (k) => (e) => setRc((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const npValid = np.name.trim() && np.client_name.trim() && np.line1.trim() && np.city.trim() && parseFloat(np.sqft) > 0;
  const rcValid = rc.name.trim() && rc.line1.trim() && rc.city.trim();
  const needsAddress = shipTo === "project" || shipTo === "other";
  const canSubmit =
    !saving &&
    lines.some((l) => l.variant_id) &&
    (mode === "showroom" || (projectId && (projectId !== NEW_PROJECT || npValid) && (!needsAddress || rcValid)));

  // V4.7 SAMPLE-160/161: authoritative server-side economics preview — loose tiles
  // draw the monthly free allowance; sheets are chargeable from the first sheet
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const valid = lines.filter((l) => l.variant_id && parseInt(l.quantity, 10) > 0);
    if (!open || !valid.length) { setPreview(null); return undefined; }
    const t = setTimeout(() => {
      api.post("/samples/preview", {
        lines: valid.map((l) => ({ variant_id: l.variant_id, quantity: parseInt(l.quantity, 10), quantity_unit: l.quantity_unit || undefined })),
      }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lines), open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let pid = projectId && projectId !== NEW_PROJECT ? projectId : "";
      if (mode === "project" && projectId === NEW_PROJECT) {
        const { data: proj } = await api.post("/projects", {
          name: np.name.trim(),
          client_name: np.client_name.trim(),
          projected_sqft: parseFloat(np.sqft),
          delivery_location: [np.line1.trim(), np.line2.trim(), `${np.city.trim()}, ${np.state.trim()} ${np.zip.trim()}`.trim()].filter(Boolean).join(", "),
          shipping_address: {
            recipient_name: np.client_name.trim(),
            line1: np.line1.trim(),
            line2: np.line2.trim(),
            city: np.city.trim(),
            state: np.state.trim(),
            postal_code: np.zip.trim(),
          },
          ...(np.project_type ? { project_type: np.project_type } : {}),
          ...(np.expected_timeframe ? { expected_timeframe: np.expected_timeframe } : {}),
          ...(np.notes.trim() ? { notes: np.notes.trim() } : {}),
        });
        pid = proj.id;
        setLocalProjects((lp) => [proj, ...lp]);
        setProjectId(proj.id);
        onProjectCreated?.(proj);
      }
      const body = {
          lines: lines.filter((l) => l.variant_id).map((l) => ({ variant_id: l.variant_id, quantity: parseInt(l.quantity, 10), quantity_unit: l.quantity_unit || undefined })),
          ...(pid ? { project_id: pid } : {}),
          ...(locationId ? { location_id: locationId } : {}),
          ...(mode === "project" && needsAddress
            ? { recipient: { name: rc.name.trim(), line1: rc.line1.trim(), line2: rc.line2.trim(), city: rc.city.trim(), state: rc.state.trim(), postal_code: rc.zip.trim(), ...(rc.phone.trim() ? { phone: rc.phone.trim() } : {}) } }
            : {}),
      };
      if (editing) {
        const { data } = await api.patch(`/samples/${editing.id}`, body);
        toast.success(data.charge_total > 0
          ? `Request updated (${data.free_quantity} free · ${data.charged_quantity} chargeable — $${data.charge_total.toFixed(2)}). Zelligio will send an updated quote.`
          : "Sample request updated");
        onOpenChange(false);
        onSubmitted?.(data);
        return;
      }
      const { data } = await api.post("/samples", body, { headers: { "Idempotency-Key": idemKey } });
      // V4.7 SAMPLE-164: chargeable requests no longer collect at submission —
      // Zelligio prepares a quote and the dealer accepts/pays it. Never redirect
      // to checkout here; surface the authoritative resulting state.
      if (data.needs_sourcing) {
        toast.info("Request submitted — one or more samples aren't currently in Florida sample inventory. Zelligio will confirm availability and timing.");
      } else if (data.charge_total > 0) {
        toast.success(`Request submitted (${data.free_quantity} free · ${data.charged_quantity} chargeable — $${data.charge_total.toFixed(2)}). Zelligio will send you a quote to review and pay.`);
      } else {
        toast.success(`Sample request submitted — ${data.free_quantity} free unit(s) this month`);
      }
      onOpenChange(false);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg sm:!max-h-[90dvh] sm:!overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">{editing ? `Edit sample request` : "New sample request"}</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">
            {editing
              ? "Change products, quantities, project or shipping details. Allowance and any charges are recalculated; Zelligio sees the update immediately."
              : "Showroom samples ship to your default showroom. Project samples can ship to the showroom or directly to your client."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 min-w-0 space-y-5" data-testid="sample-request-form">
          {ctxVariant && (
            <div className="rounded-md border border-terracotta/40 bg-sand/50 p-3 text-xs" data-testid="sample-context-banner">
              <p className="font-semibold uppercase tracking-wide text-terracotta">From {context || "product"} — prefilled</p>
              <p className="mt-1 text-charcoal"><span className="font-semibold">{ctxVariant.product_title || ctxVariant.label || productTitle(ctxVariant)}</span> · <span className="font-mono">{ctxVariant.sku}</span> · unit: {defaultUnit(ctxVariant)}</p>
              {!preselectedProjectId && <p className="mt-1 text-warmgrey">No project was linked from this context — choose one below only if it applies.</p>}
            </div>
          )}
          {lockedProject ? (
            <div className="rounded-md border border-terracotta/40 bg-sand/50 p-3 text-xs" data-testid="sample-project-context">
              <p className="font-semibold uppercase tracking-wide text-terracotta">For project — linked automatically</p>
              <p className="mt-1 text-sm font-medium text-charcoal" data-testid="sample-project-context-name">{lockedProject.name}</p>
              {(lockedProject.client_name || lockedProject.shipping_address?.line1) && (
                <p className="mt-0.5 text-warmgrey">
                  {lockedProject.client_name}{lockedProject.client_name && lockedProject.shipping_address?.line1 ? " · " : ""}
                  {lockedProject.shipping_address?.line1 ? `${lockedProject.shipping_address.line1}, ${lockedProject.shipping_address.city}${lockedProject.shipping_address.state ? `, ${lockedProject.shipping_address.state}` : ""}` : ""}
                </p>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-sand p-1" data-testid="sample-mode-toggle">
            <Button type="button" data-testid="sample-mode-showroom" onClick={() => setMode("showroom")}
              className={`h-10 transition-colors ${mode === "showroom" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal hover:bg-limestone"}`}>
              Showroom / general
            </Button>
            <Button type="button" data-testid="sample-mode-project" onClick={() => setMode("project")}
              className={`h-10 transition-colors ${mode === "project" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-transparent text-charcoal hover:bg-limestone"}`}>
              Client project
            </Button>
          </div>
          )}

          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3" data-testid={`sample-line-${i}`}>
                <div className="min-w-0 basis-full space-y-2 sm:basis-auto sm:flex-1">
                  <Label className={labelCls}>Variant</Label>
                  {l.variant_id ? (
                    <div className="flex min-h-11 items-center justify-between gap-2 rounded-md border border-border bg-white px-3 py-2" data-testid={`sample-variant-${i}`}>
                      <span className="line-clamp-2 break-words text-sm leading-snug text-charcoal" data-testid={`sample-variant-name-${i}`}>{(() => { const sv = catalog.find((v) => v.id === l.variant_id) || {}; return sv.sku ? <><span className="font-mono text-xs text-terracotta">{sv.sku}</span> · {sv.product_title || sv.label || variantIdentity(sv)}</> : "Selected variant"; })()}</span>
                      <button type="button" onClick={() => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, variant_id: "" } : x)))} className="shrink-0 text-xs font-medium text-terracotta" data-testid={`sample-variant-clear-${i}`}>Change</button>
                    </div>
                  ) : (
                    <VariantSearch catalog={catalog} onPick={(vid) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, variant_id: vid, quantity_unit: defaultUnit(catalog.find((v) => v.id === vid)) } : x)))} testid={`sample-variant-${i}`} />
                  )}
                </div>
                <div className="w-20 space-y-2">
                  <Label className={labelCls}>Qty</Label>
                  <Input type="number" min="1" max="50" data-testid={`sample-qty-${i}`} value={l.quantity} onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} className={inputCls} />
                </div>
                <div className="w-32 space-y-2">
                  <Label className={labelCls}>Unit</Label>
                  {/* ID-063: the unit follows the product spec (server-enforced) — locked once a variant is chosen */}
                  <Select disabled={!!l.variant_id} value={l.variant_id ? defaultUnit(catalog.find((v) => v.id === l.variant_id)) : (l.quantity_unit || "loose tile")} onValueChange={(v) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, quantity_unit: v } : x)))}>
                    <SelectTrigger data-testid={`sample-unit-${i}`} className="h-11 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u} data-testid={`sample-unit-${i}-${u.replace(/ /g, "-")}`}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {lines.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" data-testid={`sample-remove-line-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="text-status-sold">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="sample-add-line" onClick={() => setLines((ls) => [...ls, { variant_id: "", quantity: 1, quantity_unit: "loose tile" }])} className="border-limestone text-charcoal hover:bg-sand">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add another variant
            </Button>
          </div>

          {!lockedProject && (
          <div className="space-y-2">
            <Label className={labelCls}>Project {mode === "project" ? "*" : "(optional)"}</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="sample-project-select" className="h-11 bg-white"><SelectValue placeholder={mode === "project" ? "Select or create a project" : "No project"} /></SelectTrigger>
              <SelectContent className="bg-white">
                {mode === "project" && <SelectItem value={NEW_PROJECT} data-testid="sample-project-create-new">Create new project…</SelectItem>}
                {allProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          )}

          {pid_project_selected(projectId) ? (
            <p className="text-xs text-muted-foreground" data-testid="sample-location-inherited">
              Business location is inherited from the selected project.
            </p>
          ) : isManager ? (
            <p className="text-xs text-muted-foreground" data-testid="sample-location-fixed">
              Location: {locLabel(myLocationId) || "your assigned location"} (assigned by Zelligio)
            </p>
          ) : showLocationPicker ? (
            <div className="space-y-2">
              <Label className={labelCls}>Business location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="sample-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">The responsible showroom — separate from where this sample ships.</p>
            </div>
          ) : null}

          {mode === "project" && projectId === NEW_PROJECT && (
            <div className="space-y-4 rounded-lg border border-limestone bg-sand/40 p-4" data-testid="sample-new-project-form">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">New project</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className={labelCls}>Project name *</Label>
                  <Input data-testid="np-name" value={np.name} onChange={setNpField("name")} className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Client / contact name *</Label>
                  <Input data-testid="np-client-name" value={np.client_name} onChange={setNpField("client_name")} className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Project address *</Label>
                <Input data-testid="np-line1" value={np.line1} onChange={setNpField("line1")} placeholder="Address line 1" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="col-span-2 space-y-2">
                  <Input data-testid="np-line2" value={np.line2} onChange={setNpField("line2")} placeholder="Line 2 (suite/unit, optional)" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Input data-testid="np-city" value={np.city} onChange={setNpField("city")} placeholder="City *" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Input data-testid="np-state" value={np.state} onChange={setNpField("state")} placeholder="State" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="space-y-2">
                  <Input data-testid="np-zip" value={np.zip} onChange={(e) => { setNpField("zip")(e); zipAssist(e.target.value, np, (patch) => setNp((s) => ({ ...s, ...patch }))); }} placeholder="ZIP" className={inputCls} />
                </div>
                <div className="col-span-1 space-y-2 sm:col-span-3">
                  <Input type="number" min="1" data-testid="np-sqft" value={np.sqft} onChange={setNpField("sqft")} placeholder="Estimated square footage *" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className={labelCls}>Project type (optional)</Label>
                  <Select value={np.project_type} onValueChange={(v) => setNp((s) => ({ ...s, project_type: v }))}>
                    <SelectTrigger data-testid="np-type" className="h-11 bg-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Expected timeframe (optional)</Label>
                  <Select value={np.expected_timeframe} onValueChange={(v) => setNp((s) => ({ ...s, expected_timeframe: v }))}>
                    <SelectTrigger data-testid="np-timeframe" className="h-11 bg-white"><SelectValue placeholder="Select timeframe" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {TIMEFRAMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Notes (optional)</Label>
                <Input data-testid="np-notes" value={np.notes} onChange={setNpField("notes")} className={inputCls} />
              </div>
            </div>
          )}

          {mode === "project" && projectId !== NEW_PROJECT && (
            <div className="space-y-3" data-testid="sample-shipping-choice">
              <Label className={labelCls}>Ship this sample to *</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Button type="button" data-testid="sample-ship-showroom" onClick={() => setShipTo("showroom")}
                  className={`h-11 transition-colors ${shipTo === "showroom" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-sand text-charcoal hover:bg-limestone"}`}>
                  My showroom
                </Button>
                {projectAddr?.line1 && (
                  <Button type="button" data-testid="sample-ship-project" onClick={() => setShipTo("project")}
                    className={`h-11 transition-colors ${shipTo === "project" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-sand text-charcoal hover:bg-limestone"}`}>
                    Project address
                  </Button>
                )}
                <Button type="button" data-testid="sample-ship-other" onClick={() => { setShipTo("other"); setRc(EMPTY_RC); }}
                  className={`h-11 transition-colors ${shipTo === "other" ? "bg-terracotta text-white hover:bg-terracotta" : "bg-sand text-charcoal hover:bg-limestone"}`}>
                  Another address
                </Button>
              </div>
              {shipTo === "project" && projectAddr?.line1 && (
                <p className="text-xs text-muted-foreground" data-testid="sample-project-address-note">
                  Reusing the project address: {projectAddr.line1}, {projectAddr.city}{projectAddr.state ? `, ${projectAddr.state}` : ""} {projectAddr.postal_code} — editable below.
                </p>
              )}
            </div>
          )}

          {mode === "project" && needsAddress && (
            <div className="space-y-4 rounded-lg border border-limestone bg-sand/40 p-4" data-testid="sample-recipient-form">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Shipping address</p>
              <div className="space-y-2">
                <Label className={labelCls}>Recipient / contact name *</Label>
                <Input data-testid="rc-name" value={rc.name} onChange={setRcField("name")} className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Address *</Label>
                <Input data-testid="rc-line1" value={rc.line1} onChange={setRcField("line1")} placeholder="Address line 1" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="col-span-2 space-y-2">
                  <Input data-testid="rc-line2" value={rc.line2} onChange={setRcField("line2")} placeholder="Line 2 (optional)" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Input data-testid="rc-city" value={rc.city} onChange={setRcField("city")} placeholder="City *" className={inputCls} />
                </div>
                <div className="space-y-2">
                  <Input data-testid="rc-state" value={rc.state} onChange={setRcField("state")} placeholder="State" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="space-y-2">
                  <Input data-testid="rc-zip" value={rc.zip} onChange={(e) => { setRcField("zip")(e); zipAssist(e.target.value, rc, (patch) => setRc((s) => ({ ...s, ...patch }))); }} placeholder="ZIP" className={inputCls} />
                </div>
                <div className="col-span-1 space-y-2 sm:col-span-3">
                  <Input data-testid="rc-phone" value={rc.phone} onChange={setRcField("phone")} placeholder="Phone (optional)" className={inputCls} />
                </div>
              </div>
            </div>
          )}

          {allowance && (
            <div className="rounded-md border border-border bg-sand/50 p-3 text-xs" data-testid="sample-econ-preview">
              {preview ? (
                <div className="space-y-1" data-testid="sample-econ-server">
                  {preview.loose_requested > 0 && (
                    <p className="text-charcoal" data-testid="sample-econ-loose">
                      Loose tiles requested: <span className="font-semibold">{preview.loose_requested}</span>
                      {" · "}Free allowance applied: <span className="font-semibold" data-testid="sample-econ-free">{preview.free_total}</span>
                      {" · "}Chargeable: <span className="font-semibold" data-testid="sample-econ-chargeable">{preview.lines.filter((l) => l.quantity_unit !== "sheet").reduce((s, l) => s + l.charged_quantity, 0)}</span>
                      {preview.lines.filter((l) => l.quantity_unit !== "sheet").some((l) => l.charged_quantity > 0) && ` × $${preview.unit_charge.toFixed(2)}`}
                    </p>
                  )}
                  {preview.sheet_requested > 0 && (
                    <p className="text-charcoal" data-testid="sample-econ-sheets">
                      Sheets: <span className="font-semibold">{preview.sheet_requested}</span> × ${preview.sheet_price.toFixed(2)} (sheets are chargeable from the first sheet and never use the free allowance)
                    </p>
                  )}
                  <p className="text-warmgrey" data-testid="sample-econ-remaining">
                    Monthly free allowance remaining after this request: <span className="font-semibold text-charcoal">{preview.free_after}</span> of {preview.allowance}
                  </p>
                  {preview.charge_total > 0 && (
                    <p className="font-medium text-charcoal" data-testid="sample-econ-due">
                      Estimated material total: ${preview.charge_total.toFixed(2)} — Zelligio will send a quote for your review and secure card payment (TEST).
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-charcoal">
                  <span className="font-semibold" data-testid="sample-econ-remaining-empty">{allowance.free_remaining}</span> of {allowance.allowance} free loose-tile units left in {allowance.billing_month}
                </p>
              )}
              {lines.some((l) => catalog.find((v) => v.id === l.variant_id)?.custom_sample_72h) && (
                <p className="mt-0.5 text-[#8a6435]" data-testid="sample-custom-72h-note">
                  Custom/Morocco sample: cancellable within 72 hours of confirmation; after that it is commercially committed even if not shipped.
                </p>
              )}
            </div>
          )}

          <Button type="submit" disabled={!canSubmit} data-testid="sample-request-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Submit request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
